import { Component, effect, inject, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import {
  AccountOverrides,
  RD_STATUS,
  RecurringDetectorService,
  RecurringRow,
} from "./recurring-detector.service";
import { getMainColumns } from "./recurring-detector-columns";
import { GridOptions } from "ag-grid-community";
import { DropdownSelectMapOption } from "@bitwarden/web-vault/app/directives/dropdown.directive";
import { isRealTransaction } from "./transaction.utils";
import { GlossRouteEnum } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import {
  RecurringDetectorDetailDialogComponent,
  RecurringDetectorDetailDialogData,
} from "./recurring-detector-detail-dialog.component";

export type SpendPeriod = "month" | "quarter" | "year";

const PERIOD_MONTHS: Record<SpendPeriod, number> = { month: 1, quarter: 3, year: 12 };

// NOTE: global side-effect import (not styleUrls) to match the repo-wide convention —
// no component here uses styleUrls; the custom webpack build loads component SCSS this way.
import "./recurring-detector.component.scss";

@Component({
  selector: "app-recurring-detector",
  templateUrl: "./recurring-detector.component.html",
  host: { class: "recurring-detector" },
})
export class RecurringDetectorComponent implements OnInit {
  private stateService = inject(StateService);
  private transactionStore = inject(TransactionStoreService);
  private userStore = inject(UserStoreService);
  private detectorService = inject(RecurringDetectorService);
  private dialog = inject(MatDialog);
  private location = inject(Location);
  private router = inject(Router);

  hasAccounts = false;

  matchedRowData: RecurringRow[] = [];
  unmatchedRowData: RecurringRow[] = [];
  recurringGridOptions: GridOptions<RecurringRow> = { columnDefs: [] };
  otherGridOptions: GridOptions<RecurringRow> = { columnDefs: [] };

  totalMonthly = 0;
  monthlyCurrencySymbol = "";

  readonly periodOptions: DropdownSelectMapOption[] = [
    { realValue: "month", optionDisplayValue: "This Month", selectedDisplayValue: "This Month" },
    {
      realValue: "quarter",
      optionDisplayValue: "This Quarter",
      selectedDisplayValue: "This Quarter",
    },
    { realValue: "year", optionDisplayValue: "This Year", selectedDisplayValue: "This Year" },
  ];
  selectedPeriod: SpendPeriod = "month";
  readonly connectButtonClass =
    "tw-mt-2 tw-flex tw-items-center tw-justify-center tw-h-9 tw-w-[171px] tw-rounded-lg tw-border-none " +
    "tw-cursor-pointer tw-bg-color-action-primary hover:tw-bg-color-action-primary-hover " +
    "tw-font-body tw-text-caption tw-text-color-text-on-action tw-font-semibold";
  projectedSpend = 0;
  dueInPeriodCount = 0;

  private storageKey = "";
  private accountIds: string[] = [];

  constructor() {
    // The stores can still be loading when the page opens (e.g. a hard refresh on this route),
    // so re-derive the list whenever accounts or transactions change instead of reading once.
    effect(() => {
      const accounts = this.userStore.enrichAccounts.collection() || [];
      const transactions = this.transactionStore.enrichTransaction.collection() || [];
      this.accountIds = accounts
        .filter((acc) =>
          transactions.some(
            (tx) => tx.accountLink?.id === acc.id && isRealTransaction(tx) && tx.postDate,
          ),
        )
        .map((acc) => acc.id);
      // An account with no transaction history gives the detector nothing to read, so it
      // doesn't count — the user still needs to connect one before there's anything to show.
      this.hasAccounts = this.accountIds.length > 0;
      this.renderGrids();
    });
  }

  async ngOnInit(): Promise<void> {
    const userId = await this.stateService.getUserId();
    if (!userId) {
      return;
    }
    this.storageKey = `recurring_overrides_v2_${userId}`;

    this.recurringGridOptions = {
      columnDefs: getMainColumns(true, this.openDetail.bind(this), this.movePattern.bind(this)),
    };
    this.otherGridOptions = {
      columnDefs: getMainColumns(false, this.openDetail.bind(this), this.movePattern.bind(this)),
    };
    this.renderGrids();
  }

  get displayCurrency(): string {
    return (
      this.monthlyCurrencySymbol || this.userStore.preferences?.preferenceView()?.coreCurrency || ""
    );
  }

  get dueLabel(): string {
    return `Due ${this.periodOptions.find((o) => o.realValue === this.selectedPeriod)?.optionDisplayValue}`;
  }

  goBack(): void {
    this.location.back();
  }

  goToAccounts(): void {
    this.router.navigate([GlossRouteEnum.account]);
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period as SpendPeriod;
    this.updatePeriodSummary();
  }

  movePattern(row: RecurringRow): void {
    const overrides = this.getOverrides();
    if (!overrides[row.accountId]) {
      overrides[row.accountId] = {};
    }
    overrides[row.accountId][row.groupKey] =
      row.status === RD_STATUS.CONFIRMED ? "other" : "recurring";
    this.saveOverrides(overrides);
    this.renderGrids();
  }

  resetOverrides(): void {
    this.saveOverrides({});
    this.renderGrids();
  }

  openDetail(row: RecurringRow): void {
    const data: RecurringDetectorDetailDialogData = {
      title: `Transaction History: ${row.description}`,
      rows: this.detectorService.buildDetailRows(
        this.transactionStore.enrichTransaction.collection() || [],
        row.groupKey,
        row.accountId,
      ),
    };
    this.dialog.open(RecurringDetectorDetailDialogComponent, { data });
  }

  private renderGrids(): void {
    const { matchedRowData, unmatchedRowData } = this.detectorService.buildRowDataForAccounts(
      this.transactionStore.enrichTransaction.collection() || [],
      this.accountIds,
      this.getOverrides(),
    );
    this.matchedRowData = matchedRowData;
    this.unmatchedRowData = unmatchedRowData;

    const symbolCounts: Record<string, number> = {};
    for (const r of matchedRowData) {
      symbolCounts[r.symbol] = (symbolCounts[r.symbol] || 0) + 1;
    }
    const dominantSymbol = Object.entries(symbolCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || "";

    this.totalMonthly = matchedRowData
      .filter((r) => r.symbol === dominantSymbol)
      .reduce((s, r) => s + r.monthlyEquivalent, 0);
    this.monthlyCurrencySymbol = dominantSymbol;
    this.updatePeriodSummary();
  }

  /** Projects the recurring spend over the selected calendar period and counts the
   *  recurring payments whose next expected date falls inside it. */
  private updatePeriodSummary(): void {
    this.projectedSpend = this.totalMonthly * PERIOD_MONTHS[this.selectedPeriod];
    const { start, end } = this.getPeriodRange(this.selectedPeriod, new Date());
    this.dueInPeriodCount = this.matchedRowData.filter(
      (r) => r.nextExpected && r.nextExpected >= start && r.nextExpected < end,
    ).length;
  }

  private getPeriodRange(period: SpendPeriod, now: Date): { start: Date; end: Date } {
    const year = now.getFullYear();
    if (period === "year") {
      return { start: new Date(year, 0, 1), end: new Date(year + 1, 0, 1) };
    }
    const startMonth = period === "quarter" ? Math.floor(now.getMonth() / 3) * 3 : now.getMonth();
    return {
      start: new Date(year, startMonth, 1),
      end: new Date(year, startMonth + PERIOD_MONTHS[period], 1),
    };
  }

  private getOverrides(): Record<string, AccountOverrides> {
    if (!this.storageKey) {
      return {};
    }
    try {
      return JSON.parse(localStorage.getItem(this.storageKey) || "{}");
    } catch {
      return {};
    }
  }

  private saveOverrides(data: Record<string, AccountOverrides>): void {
    if (!this.storageKey) {
      return;
    }
    localStorage.setItem(this.storageKey, JSON.stringify(data));
  }
}
